import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, Search, MapPin } from 'lucide-react';

const COMPETITOR_OPTIONS = ['Invisalign', 'Ormco Spark', 'Dentcare Aligners', 'Smile Aligners', 'Illusion Aligners'];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateAccount = ({ onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    pincode: '',
    clinic_potential: '',
    competitor_brands: [],
    competitor_brands_others: '',
    current_sales_value: 0,
    contacts: [{
      id: Date.now().toString(),
      name: '',
      designation: '',
      whatsapp_number: '',
      email: '',
      can_influence_decisions: ''
    }]
  });

  // Google Places search state
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState([]);
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [placeFocused, setPlaceFocused] = useState(false);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  const searchPlaces = useCallback(async (query) => {
    if (query.length < 2) {
      setPlaceSuggestions([]);
      setHasSearched(false);
      return;
    }
    setSearchingPlaces(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/places/autocomplete`, {
        params: { q: query },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaceSuggestions(response.data || []);
      setHasSearched(true);
    } catch (error) {
      console.error('Places search error:', error);
      setPlaceSuggestions([]);
      setHasSearched(true);
    } finally {
      setSearchingPlaces(false);
    }
  }, []);

  const handlePlaceQueryChange = (e) => {
    const val = e.target.value;
    setPlaceQuery(val);
    setHasSearched(false);
    if (val.length < 2) {
      setPlaceSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPlaces(val), 300);
  };

  const handlePlaceKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (placeQuery.length >= 2) searchPlaces(placeQuery);
    }
  };

  const handlePlaceFocus = () => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    setPlaceFocused(true);
  };

  const handlePlaceBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setPlaceFocused(false), 300);
  };

  const selectPlace = async (place) => {
    setPlaceFocused(false);
    setPlaceSuggestions([]);
    setHasSearched(false);
    setPlaceQuery(place.description);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/places/details`, {
        params: { place_id: place.place_id },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(prev => ({
        ...prev,
        name: response.data.name,
        address: response.data.address,
        // Auto-populate city if returned from Google
        ...(response.data.city ? { city: response.data.city } : {}),
        ...(response.data.pincode ? { pincode: response.data.pincode } : {})
      }));
      const populated = ['Name', 'Address'];
      if (response.data.city) populated.push('City');
      if (response.data.pincode) populated.push(`Pincode: ${response.data.pincode}`);
      if (response.data.pincode) populated.push(`Pincode: ${response.data.pincode}`);
      toast.success(`Populated from Google: ${populated.join(', ')}!`);
    } catch (error) {
      toast.error('Could not fetch place details');
    }
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, {
        id: Date.now().toString(),
        name: '',
        designation: '',
        whatsapp_number: '',
        email: '',
        can_influence_decisions: ''
      }]
    });
  };

  const removeContact = (id) => {
    if (formData.contacts.length === 1) {
      toast.error('At least one contact is required');
      return;
    }
    setFormData({
      ...formData,
      contacts: formData.contacts.filter(c => c.id !== id)
    });
  };

  const updateContact = (id, field, value) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.map(c => 
        c.id === id ? { ...c, [field]: value } : c
      )
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/accounts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Account created successfully!');
      navigate('/accounts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8" data-testid="create-account-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create New Account
          </h1>
          <p className="text-base text-slate-600 mt-2">Add a new customer account to your CRM</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Account Details
              </h2>
              
              <div className="space-y-4">
                {/* Google Places Search */}
                <div className="relative">
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Search Clinic / Hospital
                  </Label>
                  <div className="relative mt-2 flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        value={placeQuery}
                        onChange={handlePlaceQueryChange}
                        onKeyDown={handlePlaceKeyDown}
                        onFocus={handlePlaceFocus}
                        onBlur={handlePlaceBlur}
                        className="h-12 pl-10 border-teal-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 rounded-lg bg-teal-50/30"
                        placeholder="Type clinic name and press Enter or wait..."
                        data-testid="google-places-search"
                      />
                      {searchingPlaces && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      onClick={() => { if (placeQuery.length >= 2) searchPlaces(placeQuery); }}
                      disabled={placeQuery.length < 2 || searchingPlaces}
                      className="h-12 px-4 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-lg"
                      data-testid="search-places-button"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </div>
                  {placeFocused && placeSuggestions.length > 0 && (
                    <div className="absolute z-[100] w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-xl max-h-64 overflow-y-auto" data-testid="places-dropdown">
                      {placeSuggestions.map((place) => (
                        <div
                          key={place.place_id}
                          className="flex items-start gap-3 px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors border-b border-slate-100 last:border-0"
                          onMouseDown={(e) => { e.preventDefault(); selectPlace(place); }}
                          data-testid="place-suggestion"
                        >
                          <MapPin className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-700">{place.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasSearched && placeSuggestions.length === 0 && !searchingPlaces && (
                    <div className="absolute z-[100] w-full mt-1 bg-white rounded-xl border border-slate-200 shadow-xl p-4 text-center text-sm text-slate-500">
                      No results found. Enter details manually below.
                    </div>
                  )}
                </div>

                <div className="border-t border-dashed border-slate-200 pt-4">
                  <p className="text-xs text-slate-400 mb-3">Or enter details manually:</p>
                </div>

                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Account Name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-2 h-12 border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-lg"
                    data-testid="account-name-input"
                    placeholder="Clinic or Practice Name"
                  />
                </div>

                <div>
                  <Label htmlFor="address" className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Address *
                  </Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="mt-2 min-h-32 border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-lg"
                    data-testid="account-address-input"
                    placeholder="Full address of the clinic"
                  />
                </div>

                <div>
                  <Label htmlFor="city" className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    City *
                  </Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="mt-2 h-12 border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-lg"
                    data-testid="account-city-input"
                    placeholder="City name"
                  />
                </div>

                <div>
                  <Label htmlFor="pincode" className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Pincode
                  </Label>
                  <Input
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    className="mt-2 h-12 border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-lg"
                    data-testid="account-pincode-input"
                    placeholder="6-digit pincode"
                  />
                </div>

                {/* Clinic Potential */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Clinic Potential *
                  </Label>
                  <div className="flex gap-3 mt-2">
                    {['High', 'Medium', 'Low'].map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setFormData({ ...formData, clinic_potential: opt })}
                        className={`flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                          formData.clinic_potential === opt
                            ? opt === 'High' ? 'bg-emerald-600 text-white border-emerald-600'
                            : opt === 'Medium' ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-red-500 text-white border-red-500'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Competitor Brands */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Competitor Brands Currently Used *
                  </Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {COMPETITOR_OPTIONS.map(opt => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          const curr = formData.competitor_brands || [];
                          setFormData({ ...formData, competitor_brands: curr.includes(opt) ? curr.filter(x => x !== opt) : [...curr, opt] });
                        }}
                        className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                          (formData.competitor_brands || []).includes(opt)
                            ? 'bg-teal-700 text-white border-violet-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const curr = formData.competitor_brands || [];
                        setFormData({ ...formData, competitor_brands: curr.includes('Others') ? curr.filter(x => x !== 'Others') : [...curr, 'Others'] });
                      }}
                      className={`px-3 py-2 rounded-xl border-2 text-sm font-medium transition-all ${
                        (formData.competitor_brands || []).includes('Others')
                          ? 'bg-teal-700 text-white border-violet-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-violet-400'
                      }`}
                    >
                      Others
                    </button>
                  </div>
                  {(formData.competitor_brands || []).includes('Others') && (
                    <Input
                      value={formData.competitor_brands_others}
                      onChange={e => setFormData({ ...formData, competitor_brands_others: e.target.value })}
                      placeholder="Enter other competitor brand name..."
                      className="mt-2 rounded-xl"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="current_sales_value" className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                    Current Sales Value (Rs.)
                  </Label>
                  <Input
                    id="current_sales_value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.current_sales_value}
                    onChange={(e) => setFormData({ ...formData, current_sales_value: parseFloat(e.target.value) || 0 })}
                    className="mt-2 h-12 border-slate-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 rounded-lg"
                    data-testid="current-sales-value-input"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-slate-500 mt-1">Enter the current sales value if this account already has orders</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Persons
                </h2>
                <Button
                  type="button"
                  onClick={addContact}
                  variant="outline"
                  className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50"
                  data-testid="add-contact-button"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Button>
              </div>

              <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                {formData.contacts.map((contact, index) => (
                  <div key={contact.id} className="p-4 border border-slate-200 rounded-lg space-y-3" data-testid="contact-card">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-700 uppercase tracking-wide">
                        Contact {index + 1}
                      </span>
                      {formData.contacts.length > 1 && (
                        <Button
                          type="button"
                          onClick={() => removeContact(contact.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid="remove-contact-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-slate-600">Name *</Label>
                      <Input
                        value={contact.name}
                        onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                        required
                        className="mt-1 h-10 text-sm"
                        placeholder="Contact name"
                      />
                    </div>

                    <div>
                      <Label className="text-xs text-slate-600">Designation *</Label>
                      <Select
                        value={contact.designation}
                        onValueChange={(value) => updateContact(contact.id, 'designation', value)}
                        required
                      >
                        <SelectTrigger className="mt-1 h-10 text-sm">
                          <SelectValue placeholder="Select designation" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Clinic Owner (Orthodontist)">Clinic Owner (Orthodontist)</SelectItem>
                          <SelectItem value="Clinic Owner (Non-Orthodontist)">Clinic Owner (Non-Orthodontist)</SelectItem>
                          <SelectItem value="Clinic Owning (Non-Orthodontist)">Clinic Owning (Non-Orthodontist)</SelectItem>
                          <SelectItem value="Consultant (Orthodontist)">Consultant (Orthodontist)</SelectItem>
                          <SelectItem value="Consultant (Non-Orthodontist)">Consultant (Non-Orthodontist)</SelectItem>
                          <SelectItem value="Others">Others</SelectItem>
                          <SelectItem value="Others">Others</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs text-slate-600">Phone Number(s) *</Label>
                      <Input
                        value={contact.whatsapp_number}
                        onChange={(e) => updateContact(contact.id, 'whatsapp_number', e.target.value)}
                        required
                        className="mt-1 h-10 text-sm"
                        placeholder="Phone number(s)"
                      />
                    </div>

                    <div>
                      <Label className="text-xs text-slate-600">Email ID</Label>
                      <Input
                        type="email"
                        value={contact.email || ''}
                        onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                        className="mt-1 h-10 text-sm"
                        placeholder="Optional email"
                      />
                    </div>

                    <div>
                        <Label className="text-xs text-slate-600">Influence on Purchase Decision? *</Label>
                        <Select
                          value={contact.can_influence_decisions}
                          onValueChange={(value) => updateContact(contact.id, 'can_influence_decisions', value)}
                          required
                        >
                          <SelectTrigger className="mt-1 h-10 text-sm">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="High">High</SelectItem>
                            <SelectItem value="Medium">Medium</SelectItem>
                            <SelectItem value="Low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center space-x-4">
            <Button
              type="submit"
              disabled={loading}
              className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm rounded-lg px-8 py-3 font-medium transition-all active:scale-95"
              data-testid="submit-account-button"
            >
              {loading ? 'Creating...' : 'Create Account'}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/accounts')}
              variant="outline"
              className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateAccount;
